import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('Verifying your payment...');
  const [tier, setTier] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
      setMessage('Invalid payment session');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      setMessage('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setMessage(response.data.message || 'Payment successful!');
        setTier(response.data.tier);
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        setMessage('Payment session expired. Please try again.');
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  const getTierName = (tierId) => {
    const names = {
      'premium': 'Premium',
      'pro': 'Pro'
    };
    return names[tierId] || tierId;
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4">
      <div className="glass-card p-8 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="h-16 w-16 text-[#D4AF37] mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-bold text-white mb-2">Processing Payment</h1>
            <p className="text-slate-400">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
            <p className="text-slate-400 mb-4">{message}</p>
            {tier && (
              <div className="inline-block px-4 py-2 bg-[#D4AF37]/20 rounded-full mb-6">
                <span className="text-[#D4AF37] font-medium">
                  Upgraded to {getTierName(tier)}
                </span>
              </div>
            )}
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]"
            >
              Go to Dashboard
            </Button>
          </>
        )}

        {status === 'expired' && (
          <>
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-2">Session Expired</h1>
            <p className="text-slate-400 mb-6">{message}</p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]"
            >
              Return to Dashboard
            </Button>
          </>
        )}

        {status === 'timeout' && (
          <>
            <Loader2 className="h-16 w-16 text-amber-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-2">Verification Timeout</h1>
            <p className="text-slate-400 mb-6">{message}</p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]"
            >
              Return to Dashboard
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-2">Error</h1>
            <p className="text-slate-400 mb-6">{message}</p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]"
            >
              Return to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
